import React, {
    Fragment,
    useEffect,
    useState
} from 'react';
import {useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {Button, Col, Container, Row} from 'react-bootstrap';
import {
    ConfirmationDialog
} from "../../Common/ConfirmationDialog";
import {
    GenericModelViewer
} from "../../Common/GenericModelViewer";
import {
    Glyphicon
} from "../../Common/GlyphIcon";
import {
    ObjectUtils
} from "../../../Utils/ObjectUtils";
import {
    Program
} from "../../../Models/StreamBank/Program";

import {service as ProgramService} from "../../../Services/ProgramService";
import {service as ProjectService} from "../../../Services/ProjectService";
import {service as ContextNodeService} from "../../../Services/ContextNodeService";
import {service as SurveySubmissionService} from "../../../Services/SurveySubmissionService";
import {surveyTagToBadge, rollupCountToBadge, useSubmissionStatusPresentation} from "../../../Utils/surveyTagUtils";

import {
    useChangeContext,
	useChangeContextAndTool,
} from "../../../Hooks/RouteHooks";
import {
	useShowPermissionDenied
} from "../../../Hooks/ErrorHooks";
import {
	useEditContextNodeId,
	usePermission,
} from "../../../Hooks/PermissionHooks";
import {useMakePageRequest} from "../../../Hooks/RequestHooks";

import {useContextBuilder} from "../../../Hooks/ContextBuilderHooks";
import {useClassNames} from "../../../Hooks/StyleHooks";

import {ChildrenList} from "../../Common/ChildrenList/ChildrenList";
import {ContextNodeBenefitSummarySection} from "../../Common/BenefitSummary/ContextNodeBenefitSummarySection";

import {InfoIcon} from "../../Common/InfoIcon";
import {QuickLinkListItem} from "../../Common/ChildrenList/QuickLinkListItem";
import {ContextNodeChildrenList} from "../../Common/ChildrenList/ContextNodeChildrenList";
import {EditableContextNodeListItem} from "../../Common/ChildrenList/EditableContextNodeListItem";
import {useEffectPromise} from "../../../Hooks/EffectHooks";
import {ContextNodeMap} from "../../Common/Map/ContextNodeMap";
import {useValidations} from "../../../Hooks/GenericModelHooks";
import {ProjectImportModal} from "../SpeciesLists/Common/Modals/ProjectImportModal";
import {useFilterByProjectStatus} from "../../../Hooks/useFilterByProjectStatus";
import {ProjectContextNodeChildrenList, INACTIVE_PROJECT_STATUS_CODES} from "../../Common/ChildrenList/ProjectContextNodeChildrenList";
import {SubProgramContextNodeChildrenList} from "../../Common/ChildrenList/SubProgramContextNodeChildrenList";
import {GlyphButton} from "../../Common/ButtonWithIcon";
import {useSafeState} from "../../../Hooks/useSafeState";


let model = new Program();

export const ProgramsViewPage = () => {
    const context = useSelector(state => state.contextState.context);
    const currentUser = useSelector(state => state.userState.user);

    const classNames = useClassNames('sb-programs-view-page');
    const contextBuilder = useContextBuilder();
    const permission = usePermission();
    const showPermissionDenied = useShowPermissionDenied();
    const editContextNodeId = useEditContextNodeId();
    const changeContext = useChangeContext();
    const changeContextAndTool = useChangeContextAndTool();
    const makePageRequest = useMakePageRequest();
    
    const [program, setProgram] = useState(null);
    const [childrenProjects, setChildrenProjects] = useSafeState([]);
    const [surveyTagData, setSurveyTagData] = useSafeState(null);
    const [existingProgram, setExistingProgram] = useState(null);
    const [validations, setValidations, isValid] = useValidations();
    const [editMode, setEditMode] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [showProjectImportModal, setShowProjectImportModal] = useState(false);

    // Inactive projects are hidden on the map until revealed via the project
    // list's "Show N Inactive Projects" toggle, then drawn grayed out (SB-3294)
    const [showInactiveProjects, setShowInactiveProjects] = useState(false);
    const filterInactiveProjects = useFilterByProjectStatus(INACTIVE_PROJECT_STATUS_CODES);

    const contextNodeId = program?.contextNodeId;
    const programId = (context.childProgram === "new" || context.parentProgram === "new")
        ? "new"
        : (context.childProgramId || context.programId);
    
    const setEditModeWithParentProgram = (parentProgram) => {
        setEditMode(true);
        setProgram({
            parentAssignedSpeciesListId: parentProgram?.assignedSpeciesListId,
            organizationId: parentProgram?.organizationId ?? currentUser?.organizationId,
            parentProgramId: parentProgram?.programId ?? null
        });
        setChildrenProjects([]);
    }

    useEffectPromise(
        async () => (await ProgramService.GetProgram(programId))?.result ?? {},
        (program) => {
            setProgram(program);
            setEditMode(false);
        },
        [programId],
        programId !== null && programId !== "new"
    );
    
    const reloadChildrenProjects = () => {
        // forces a reload of the children projects list
        contextBuilder();
        // forces a reload of the map projects
        void loadChildrenProjects();
    }
    
    /* for the map */
    const loadChildrenProjects = async () => {
        if(programId == null || programId === "new") {
            return;
        }
        setChildrenProjects(null);
        try {
            const childrenProjects = (await ProjectService.GetChildrenProjects(programId))?.result ?? [];
            setChildrenProjects(childrenProjects);
        } catch {
            setChildrenProjects([]);
        }
    }
    
    useEffect(() => {
        void loadChildrenProjects();
    },[programId]);

    const isReviewer = permission('review_survey_submissions');

    // The resolver also primes the status reference cache this page now needs —
    // getForParent sends status codes only, and badge labels resolve from the cache.
    const presentStatus = useSubmissionStatusPresentation(isReviewer);

    // Per-project tags and per-sub-program rollup counts are computed server-side
    // (getForParent); the page just renders them.
    useEffect(() => {
        if (!programId || programId === 'new') {
            setSurveyTagData(null);
            return;
        }
        void (async () => {
            try {
                const result = (await SurveySubmissionService.GetForParent(programId))?.result ?? null;
                setSurveyTagData(result);
            } catch (err) {
                console.error('Failed to load survey status tags for program list', err);
                setSurveyTagData(null);
            }
        })();
    }, [programId]);

    const tagsByContextNodeId = surveyTagData?.tagsByContextNodeId ?? {};
    const rollupCountsBySubProgramContextNodeId = surveyTagData?.rollupCountsBySubProgramContextNodeId ?? {};

    useEffect(() => {
        if(programId === "new") {
            if (!permission('create_program')) {
                showPermissionDenied();
            } else {
                let promises = [];
                if (context.parentProgram !== null && context.parentProgram !== "new") {
                    promises.push(
                        ContextNodeService.Lookup({
                                programId: context.parentProgram
                            }
                        ));
                }
                
                void makePageRequest(
                    () => Promise.all(promises),
                    ([contextNodeLookup]) => {
                        setEditModeWithParentProgram(contextNodeLookup?.program);
                    }
                );
            }
        }
    }, [programId]);

    const canEdit = () => {
        if(program) {
            if(permission('update_program')) {
                return editContextNodeId(program.contextNodeId);
            }
        }
        return false;
    };

    const canDelete = () => {
        if(program) {
            if(permission('delete_program')) {
                return editContextNodeId(program.contextNodeId);
            }
        }

        return false;
    };

    const enterEditMode = () => {
        setEditMode(true);
        setExistingProgram(ObjectUtils.Clone(program));
    };

    const exitEditMode = () => {
        if(existingProgram) {
            setEditMode(false);
            setProgram(ObjectUtils.Clone(existingProgram));
            setExistingProgram(null);
        } else {
            if(context.childProgram === 'new') {
                changeContext({childProgram: null});
            } else {
                changeContext({parentProgram: null});
            }
        }
    };

    const updateProgram = (program) => {
        setProgram(program);
    };

    const params = useParams();

    const saveProgram = () => {
        if(program.programId) {
            makePageRequest(
                _ => ProgramService.UpdateProgram(program),
                x => {
                    setEditMode(false);
                    setProgram(x.result);
                    validateSlug(x.result);

                    // TODO: update breadcrumbs from redux store
                    // instead of rebuilding context
                    contextBuilder();
                },
            );
        } else {
            makePageRequest(
                _ => ProgramService.CreateProgram(program),
                x => {
                    if(x.result.parentProgramId) {
                        changeContextAndTool(
                            {
                                parentProgram: params.pProgId,
                                childProgram: x.result.slug,
                                project: null,
                                contact: null
                            },
                            "summary"
                        );
                    } else {
                        changeContextAndTool(
                            {
                                parentProgram: x.result.slug,
                                childProgram: null,
                                project: null,
                                contact: null
                            },
                            "summary"
                        );
                    }
                },
            );
        }
    };

    const deleteProgram = () => {
        makePageRequest(
            () => ProgramService.DeleteProgram(program.programId),
            (x) => {
                if(context.childProgram !== null) {
                    changeContext({childProgram: null});
                } else {
                    changeContext({parentProgram: null});
                }

                setDeleteDialog(false);
            },
        );
    };

    const getObjTitle = () => {
        if(program && program.parentProgramId) {
            return "Sub-Program";
        }

        return "Program";
    };

    const validateSlug = (program) => {
        if(program && changeContextAndTool) {
            //if the slug doesn't match, we should update the url.
            if(program.parentProgramId) {
                if(
                    params.pProgId !== program.parentSlug ||
                    params.cProgId !== program.slug
                ) {
                    changeContextAndTool(
                        {
                            parentProgram: program.parentSlug,
                            childProgram: program.slug,
                            project: null,
                            contact: null
                        },
                        params.tool
                    );
                }
            } else {
                if(params.pProgId !== program.slug) {
                    changeContextAndTool(
                        {
                            parentProgram: program.slug,
                            childProgram: null,
                            project: null,
                            contact: null
                        },
                        params.tool
                    );
                }
            }
        }
    };


    // don't render page before it is loaded
    if(program === null) {
        return null;
    }

    return <Container className={classNames}>
        <Row>
            <ProjectImportModal
                contextNodeId={contextNodeId}
                show={showProjectImportModal}
                onHide={() => setShowProjectImportModal(false)}
                onReload={reloadChildrenProjects}
            />
            <ConfirmationDialog
                Title="Confirmation"
                Body={"Are you sure you want to delete this " + getObjTitle() + "?"}
                Display={deleteDialog}
                OnClose={() => {
                    setDeleteDialog(false);
                }}
                OnConfirm={() => {
                    deleteProgram();
                }}
            />
            <Col xs={12} md={6}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? [
                                    <Button
                                        variant="save"
                                        key={1}
                                        onClick={() => saveProgram()}
                                        disabled={!isValid}
                                    >
                                        Save
                                    </Button>,
                                    <Button
                                        variant="cancel"
                                        key={2}
                                        onClick={() => exitEditMode()}
                                    >
                                        Cancel
                                    </Button>,
                                    programId !== 'new' && canDelete() ?
                                        <Button
                                            variant="delete"
                                            key={3}
                                            onClick={() => setDeleteDialog(true)}
                                        >
                                            Delete
                                        </Button> :
                                        null
                                ] :
                                (
                                    canEdit() ?
                                        <Button
                                            variant="link"
                                            onClick={() => enterEditMode()}>
                                            <Glyphicon glyph="pencil">Edit</Glyphicon>
                                        </Button>:
                                        null
                                )
                        }
                    </div>
                    {program && program.name ? program.name: getObjTitle()}
                    <InfoIcon link={program.infoLink}>{program.infoTitle}</InfoIcon>
                </h2>

                {
                    !editMode && program ? [
                            program.goalsDescription ? <h3 key="1">Program Goals</h3> : null,
                            program.goalsDescription ? <div className="text-summary pndlm-line-break" key="2">{program.goalsDescription}</div> : null,
                            program.areaDescription ? <h3 key="3">Program Area</h3> : null,
                            program.areaDescription ? <div className="text-summary pndlm-line-break" key="4">{program.areaDescription}</div> : null,
                        ] :
                        null
                }

                {
                    program ?
                        <GenericModelViewer
                            IsNewModel={programId === "new"}
                            Model={model}
                            EditMode={editMode}
                            Data={program}
                            Update={updateProgram}
                            OnValidationUpdate={setValidations}
                            HideGroups={[]}
                            CustomData={{
                                organizationId: program?.organizationId ?? currentUser?.organizationId
                            }}
                        />:
                        null
                }
                <ContextNodeBenefitSummarySection contextNodeId={contextNodeId} editMode={editMode} />
            </Col>
            
            { program && programId !== 'new' ?
            <Col xs={12} md={{span: 5, offset:1}}>
                {
                    context?.childProgram ?
                        <ProjectContextNodeChildrenList
                            context={context}
                            revealed={showInactiveProjects}
                            onRevealedChange={setShowInactiveProjects}
                            showActions={canEdit()}
                            actions={
                                <Fragment>
                                    <GlyphButton
                                        children="Add Project"
                                        className="h3"
                                        glyph="plus-sign"
                                        onClick={() => { changeContext({ project: 'new'}) }}
                                    />
                                    { permission('import_project') ?
                                        <GlyphButton
                                            children="Import Projects"
                                            className="h3"
                                            glyph="plus-sign"
                                            onClick={() => { setShowProjectImportModal(true); }}
                                        /> : null
                                    }
                                </Fragment>
                            }
                            render={(item, i) => (
                                <EditableContextNodeListItem
                                    key={i}
                                    item={item}
                                    badge={surveyTagToBadge(tagsByContextNodeId[item.contextNodeId] ?? null, item?.link, presentStatus)}
                                />
                            )}
                        /> :
                        <SubProgramContextNodeChildrenList
                            context={context}
                            showActions={canEdit()}
                            actions={
                                <GlyphButton
                                    children="Add Sub-Program"
                                    glyph="plus-sign"
                                    onClick={() => { changeContext({ childProgram: 'new'}) }}
                                />
                            }
                            render={(item, i) => (
                                <EditableContextNodeListItem
                                    key={i}
                                    item={item}
                                    badge={rollupCountToBadge(rollupCountsBySubProgramContextNodeId[item.contextNodeId] ?? null, isReviewer)}
                                />
                            )}
                        />
                }

                <ChildrenList
                    title="Quick Links"
                    items={program?.ContextNodeQuickLinks}
                    hidden={!program?.ContextNodeQuickLinks?.length}
                    sort={(a, b) => {
                        if (a.order < b.order) {
                            return -1;
                        } else if (a.order > b.order) {
                            return 1;
                        }

                        if (a.title < b.title) {
                            return -1;
                        } else if (a.title > b.title) {
                            return 1;
                        }

                        return 0;
                    }}
                    itemComponent={QuickLinkListItem}
                />
                
                <ContextNodeMap
                    contextNodeId={program?.contextNodeId}
                    contextNodeTypeId={3}
                    items={childrenProjects ?? []}
                    mutedFilter={filterInactiveProjects}
                    showMuted={showInactiveProjects}
                />
            </Col>
                
                : null
            }
        </Row>
    </Container>;
};
